import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { LibraryFaq, CreateFaqDto, UpdateFaqDto } from '../../types/library';

interface FaqDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFaqDto | UpdateFaqDto) => void;
  faq?: LibraryFaq | null;
}

const FaqDialog: React.FC<FaqDialogProps> = ({ open, onClose, onSubmit, faq }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    if (faq) {
      setQuestion(faq.question);
      setAnswer(faq.answer);
      setCategory(faq.category || '');
    } else {
      setQuestion('');
      setAnswer('');
      setCategory('');
    }
  }, [faq, open]);

  const normalizeCategory = (cat: string): string => {
    // Preserve casing — acronyms like ISO, API, CO₂ must round-trip intact.
    return cat.trim();
  };

  const handleSubmit = () => {
    if (!question.trim() || !answer.trim()) return;
    const normalizedCategory = normalizeCategory(category);
    onSubmit({
      question: question.trim(),
      answer: answer.trim(),
      category: normalizedCategory || undefined,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{faq ? 'Edit FAQ' : 'Create FAQ'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
          />
          <TextField
            label="Answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
          />
          <TextField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
            placeholder="e.g., General, Technical, Billing"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!question.trim() || !answer.trim()}>
          {faq ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaqDialog;
